import { Action, State } from './controllers/app.controller';
import { Alert, Heading, Pane, Paragraph, Pre, Spinner, Text } from 'evergreen-ui';
import React, { FC } from 'react';
import { getClientsFullTree, getClientsFullTreeV2 } from './api';

import { Client } from './models';
import { ClientStatisticsComponent } from './components/client-stats.component';
import { DownloadMembersButton } from './components/download-members-button.component';
import { EmployeeList } from './components/employee-list.component';
import { EmployeeListActionsComponent } from './components/employee-list-actions';
import { MemberGroupComponent } from './components/member-group.component';
import { useFlags } from './feature-flags/main';

interface MainViewLayoutProps {
  sideContent?: React.ReactNode;
}

const ViewHeader: FC<ViewHeaderProps> = ({ client }) => (
  <Pane
    display="flex"
    flexDirection="row"
    alignItems="baseline"
    background="tint1"
    gap={8}
    marginRight={16}
    justifyContent="space-between"
  >
    <Heading size={800}>{client.name}</Heading>
    <Pane display="flex" alignItems="baseline">
      <ClientStatisticsComponent memberGroups={client.memberGroups} />
      <DownloadMembersButton client={client} />
    </Pane>
  </Pane>
);

const MainViewLayout: FC<MainViewLayoutProps> = ({ children, sideContent }) => (
  <Pane display="flex" flexDirection="row" background="tint1" paddingTop={16}>
    <Pane is="main" flex="1 0 auto" display="flex" flexDirection="column" gap={8} marginRight={16}>
      {children}
    </Pane>
    {sideContent ? (
      <Pane
        is="aside"
        flex="0 1 600px"
        paddingLeft={16}
        maxHeight="calc(100vh - 116px - var(--deprecation-banner-height, 0px))"
        overflowY="scroll"
        position="sticky"
        top={0}
      >
        {sideContent}
      </Pane>
    ) : null}
  </Pane>
);

const MainViewLayoutWithHeader: FC<MainViewLayoutProps & { client?: Client }> = ({ children, sideContent, client }) => (
  <Pane background="tint1" padding={16} paddingRight={0}>
    {client && <ViewHeader client={client} />}
    <MainViewLayout sideContent={sideContent}>{children}</MainViewLayout>
  </Pane>
);

interface ViewHeaderProps {
  client: Client;
  children?: never;
}

export interface MainViewProps extends State {
  dispatch: React.Dispatch<Action>;
  children?: never;
}

const EmptySideContent: FC = () => (
  <Pane display="flex" flexDirection="column" height="100%" alignContent="center" justifyContent="center">
    <Paragraph textAlign="center">Select an employee to edit their details.</Paragraph>
  </Pane>
);

export const ErrorPage: FC<{ displayError: Error | undefined }> = ({ displayError }) => (
  <MainViewLayout>
    <Alert intent="danger" title="An error has occurred">
      <Paragraph>Please refresh the page.</Paragraph>
      {displayError ? (
        <>
          <Heading size={400} marginTop={8}>
            Technical details
          </Heading>
          <Pre>{displayError.stack}</Pre>
        </>
      ) : null}
    </Alert>
  </MainViewLayout>
);

export const MainView: FC<MainViewProps> = ({
  status,
  dispatch,
  client,
  selectedMemberGroup,
  editSerial,
  ssnDB,
  searchKeySubmitted,
  searchGroup,
  error: displayError,
}) => {
  const { 'membership-single-source-of-truth-in-eligibility': membershipSingleSourceOfTruthInEligibility } = useFlags();

  switch (status) {
    case 'loading':
      return (
        <MainViewLayout>
          <Spinner delay={500} marginX="auto" marginY="120" />
        </MainViewLayout>
      );
    case 'list':
      return (
        <MainViewLayoutWithHeader sideContent={<EmptySideContent />} client={client!}>
          <EmployeeListActionsComponent dispatch={dispatch} searchGroup={searchGroup} />
          <EmployeeList
            memberGroups={client!.memberGroups}
            dispatch={dispatch}
            searchKey={searchKeySubmitted}
            searchGroup={searchGroup}
          />
        </MainViewLayoutWithHeader>
      );
    case 'editSelected':
      return (
        <MainViewLayoutWithHeader
          client={client!}
          sideContent={
            <MemberGroupComponent
              key={`edit-member-group-${editSerial}`}
              client={client!}
              memberGroup={selectedMemberGroup}
              ssnDB={ssnDB}
              onIsDirtyChanged={isDirty => dispatch({ type: 'updateMemberGroupIsDirty', isDirty })}
              onSaveComplete={selectedMemberGroupId => {
                if (membershipSingleSourceOfTruthInEligibility) {
                  return getClientsFullTreeV2(client!.id).then(results =>
                    dispatch({
                      type: 'receivedClientFullTreeResults',
                      results,
                      selectedMemberGroupId,
                      isMembershipSingleSourceOfTruthInEligibility: true,
                    }),
                  );
                } else {
                  return getClientsFullTree(client!.id).then(
                    (
                      results, // pass clients or plans into this call
                    ) =>
                      dispatch({
                        type: 'receivedClientFullTreeResults',
                        results,
                        selectedMemberGroupId,
                        isMembershipSingleSourceOfTruthInEligibility: false,
                      }),
                  );
                }
              }}
              onSaveError={error => dispatch({ type: 'error', error })}
              onClose={() => dispatch({ type: 'closeMemberGroup' })}
            />
          }
        >
          <EmployeeListActionsComponent dispatch={dispatch} searchGroup={searchGroup} />
          <EmployeeList
            memberGroups={client!.memberGroups}
            selectedMemberGroupId={selectedMemberGroup?.id}
            dispatch={dispatch}
            searchKey={searchKeySubmitted}
            searchGroup={searchGroup}
          />
        </MainViewLayoutWithHeader>
      );
    case 'newMemberGroup':
      return (
        <MainViewLayoutWithHeader
          client={client!}
          sideContent={
            <MemberGroupComponent
              key={`new-member-group-${editSerial}`}
              client={client!}
              ssnDB={ssnDB}
              onIsDirtyChanged={isDirty => dispatch({ type: 'updateMemberGroupIsDirty', isDirty })}
              onSaveComplete={selectedMemberGroupId => {
                if (membershipSingleSourceOfTruthInEligibility) {
                  getClientsFullTreeV2(client!.id).then(results =>
                    dispatch({
                      type: 'receivedClientFullTreeResults',
                      results,
                      selectedMemberGroupId,
                      isMembershipSingleSourceOfTruthInEligibility: true,
                    }),
                  );
                } else {
                  getClientsFullTree(client!.id).then(results =>
                    dispatch({
                      type: 'receivedClientFullTreeResults',
                      results,
                      selectedMemberGroupId,
                      isMembershipSingleSourceOfTruthInEligibility: false,
                    }),
                  );
                }
              }}
              onSaveError={error => dispatch({ type: 'error', error })}
              onClose={() => dispatch({ type: 'closeMemberGroup', shouldDiscardChanges: true })}
            />
          }
        >
          <EmployeeListActionsComponent dispatch={dispatch} searchGroup={searchGroup} />
          <EmployeeList
            memberGroups={client!.memberGroups}
            dispatch={dispatch}
            searchKey={searchKeySubmitted}
            searchGroup={searchGroup}
          />
        </MainViewLayoutWithHeader>
      );
    case 'error':
      return <ErrorPage displayError={displayError} />;
    case 'error:forbidden':
      return (
        <MainViewLayout>
          <Text>You do not have access to this page.</Text>
        </MainViewLayout>
      );
  }
};
